import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Shield, FileText, Plus, Edit2, Trash2, UserPlus, Bell, CheckCircle, Settings as SettingsIcon, Send, Save, ExternalLink, AlertCircle, Sparkles, DollarSign, RotateCcw, KeyRound, X, Copy, Download, Calendar } from 'lucide-react';

// ============ GATE 2 ============
export const Gate2 = () => {
  const { isReadOnly } = useAuth();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    qualification_date: new Date().toISOString().split('T')[0],
    prospect_name: '', prospect_company: '', prospect_vertical: '',
    box1_money: '', box1_status: 'empty',
    box2_decision_maker: '', box2_decision_process: '', box2_status: 'empty',
    box3_pain: '', box3_compelling_event: '', box3_status: 'empty',
    box4_proof_level: 'none', box4_proof_details: '', box4_trust_plan: '',
    box5_section2_test: '', box5_status: 'no',
    competition_notes: '', decision: 'hold', decision_reasoning: '',
    next_action: '', next_action_deadline: ''
  });

  useEffect(() => {
    api.get('/gate2').then(r => setList(r.data.qualifications || [])).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await api.put(`/gate2/${selected}`, form);
      } else {
        await api.post('/gate2', form);
      }
      const res = await api.get('/gate2');
      setList(res.data.qualifications || []);
      setShowForm(false);
      setSelected(null);
    } catch (err) { alert('Error saving Gate 2'); }
  };

  const edit = (q) => {
    setForm({
      qualification_date: q.qualification_date?.split('T')[0] || '',
      prospect_name: q.prospect_name || '', prospect_company: q.prospect_company || '',
      prospect_vertical: q.prospect_vertical || '',
      box1_money: q.box1_money || '', box1_status: q.box1_status || 'empty',
      box2_decision_maker: q.box2_decision_maker || '', box2_decision_process: q.box2_decision_process || '',
      box2_status: q.box2_status || 'empty',
      box3_pain: q.box3_pain || '', box3_compelling_event: q.box3_compelling_event || '',
      box3_status: q.box3_status || 'empty',
      box4_proof_level: q.box4_proof_level || 'none', box4_proof_details: q.box4_proof_details || '',
      box4_trust_plan: q.box4_trust_plan || '',
      box5_section2_test: q.box5_section2_test || '', box5_status: q.box5_status || 'no',
      competition_notes: q.competition_notes || '', decision: q.decision || 'hold',
      decision_reasoning: q.decision_reasoning || '',
      next_action: q.next_action || '', next_action_deadline: q.next_action_deadline?.split('T')[0] || ''
    });
    setSelected(q.id);
    setShowForm(true);
  };

  const decisionBadge = (d) => {
    const map = { proposal: 'badge-green', hold: 'badge-amber', drop: 'badge-red' };
    return <span className={`badge ${map[d] || 'badge-gray'}`}>{d?.toUpperCase()}</span>;
  };

  const statusBadge = (s) => {
    const map = { strong: 'badge-green', weak: 'badge-amber', empty: 'badge-red', yes: 'badge-green', mostly: 'badge-amber', no: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const BoxField = ({ num, title, children }) => (
    <div style={{ background: '#F7F9FB', padding: 16, borderRadius: 8, marginBottom: 16, borderLeft: '4px solid #2E75B6' }}>
      <h4 style={{ color: '#1F3A5F', marginBottom: 12, fontSize: 14 }}>Box {num} — {title}</h4>
      {children}
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h2><Shield size={24} style={{ marginRight: 8 }} /> Gate 2 — Post-Call Qualification</h2>
          <p>Fill after every discovery call. Decide: Proposal, Hold, or Drop.</p>
        </div>
        {!isReadOnly && (
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setSelected(null); }}>
            <Plus size={16} /> New Qualification
          </button>
        )}
      </div>

      {!isReadOnly && showForm && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>{selected ? 'Edit' : 'New'} Qualification</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group"><label>Date</label><input type="date" value={form.qualification_date} onChange={e => setForm({...form, qualification_date: e.target.value})} required /></div>
              <div className="form-group"><label>Prospect Name</label><input value={form.prospect_name} onChange={e => setForm({...form, prospect_name: e.target.value})} required /></div>
              <div className="form-group"><label>Company</label><input value={form.prospect_company} onChange={e => setForm({...form, prospect_company: e.target.value})} /></div>
              <div className="form-group"><label>Vertical</label><input value={form.prospect_vertical} onChange={e => setForm({...form, prospect_vertical: e.target.value})} /></div>
            </div>

            <BoxField num={1} title="Money (VETO POWER)">
              <div className="form-group"><label>Where is the money coming from? How much committed?</label>
                <textarea value={form.box1_money} onChange={e => setForm({...form, box1_money: e.target.value})} rows={3} /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.box1_status} onChange={e => setForm({...form, box1_status: e.target.value})}>
                  <option value="strong">Strong</option><option value="weak">Weak</option><option value="empty">Empty</option>
                </select></div>
            </BoxField>

            <BoxField num={2} title="Decision Maker + Process">
              <div className="form-group"><label>Who signs off? Were they on the call?</label>
                <textarea value={form.box2_decision_maker} onChange={e => setForm({...form, box2_decision_maker: e.target.value})} rows={2} /></div>
              <div className="form-group"><label>What happens between proposal and signed contract?</label>
                <textarea value={form.box2_decision_process} onChange={e => setForm({...form, box2_decision_process: e.target.value})} rows={2} /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.box2_status} onChange={e => setForm({...form, box2_status: e.target.value})}>
                  <option value="strong">Strong</option><option value="weak">Weak</option><option value="empty">Empty</option>
                </select></div>
            </BoxField>

            <BoxField num={3} title="Pain + Compelling Event">
              <div className="form-group"><label>What happens if this doesn't get built?</label>
                <textarea value={form.box3_pain} onChange={e => setForm({...form, box3_pain: e.target.value})} rows={2} /></div>
              <div className="form-group"><label>Specific event/deadline forcing the build</label>
                <input value={form.box3_compelling_event} onChange={e => setForm({...form, box3_compelling_event: e.target.value})} placeholder="Launch date, funding deadline, contract expiry..." /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.box3_status} onChange={e => setForm({...form, box3_status: e.target.value})}>
                  <option value="strong">Strong</option><option value="weak">Weak</option><option value="empty">Empty</option>
                </select></div>
            </BoxField>

            <BoxField num={4} title="Proof">
              <div className="form-row">
                <div className="form-group"><label>Proof Level</label>
                  <select value={form.box4_proof_level} onChange={e => setForm({...form, box4_proof_level: e.target.value})}>
                    <option value="strong">Strong — direct case study</option>
                    <option value="adjacent">Adjacent — related work</option>
                    <option value="none">None — selling skills</option>
                  </select></div>
              </div>
              <div className="form-group"><label>Proof Details</label>
                <textarea value={form.box4_proof_details} onChange={e => setForm({...form, box4_proof_details: e.target.value})} rows={2} placeholder="Which case study/project?" /></div>
              {form.box4_proof_level === 'none' && (
                <div className="form-group"><label>Trust-Building Plan (required when None)</label>
                  <textarea value={form.box4_trust_plan} onChange={e => setForm({...form, box4_trust_plan: e.target.value})} rows={2} placeholder="How are we building trust instead?" /></div>
              )}
            </BoxField>

            <BoxField num={5} title="Section 2 Test">
              <div className="form-group"><label>Can you write "What we heard you say" right now, confidently?</label>
                <textarea value={form.box5_section2_test} onChange={e => setForm({...form, box5_section2_test: e.target.value})} rows={4}
                  placeholder="Buyer's problem, current state, desired state, success criteria, constraints..." /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.box5_status} onChange={e => setForm({...form, box5_status: e.target.value})}>
                  <option value="yes">Yes — all five subsections clear</option>
                  <option value="mostly">Mostly — one gap needs follow-up</option>
                  <option value="no">No — can't write it confidently</option>
                </select></div>
            </BoxField>

            <div className="form-group"><label>Competition / Alternatives (if mentioned)</label>
              <input value={form.competition_notes} onChange={e => setForm({...form, competition_notes: e.target.value})} placeholder="Who else are they talking to?" /></div>

            <div style={{ background: '#F3F4F6', padding: 20, borderRadius: 8, marginTop: 16 }}>
              <h4 style={{ marginBottom: 12 }}>Decision</h4>
              <div className="form-row">
                <div className="form-group"><label>Verdict</label>
                  <select value={form.decision} onChange={e => setForm({...form, decision: e.target.value})} style={{ fontWeight: 700, fontSize: 16 }}>
                    <option value="proposal">✅ PROPOSAL</option>
                    <option value="hold">⏸ HOLD</option>
                    <option value="drop">🛑 DROP</option>
                  </select></div>
                <div className="form-group"><label>Next Action Deadline</label>
                  <input type="date" value={form.next_action_deadline} onChange={e => setForm({...form, next_action_deadline: e.target.value})} /></div>
              </div>
              <div className="form-group"><label>Reasoning</label>
                <textarea value={form.decision_reasoning} onChange={e => setForm({...form, decision_reasoning: e.target.value})} rows={2} /></div>
              <div className="form-group"><label>Next Action (within 48 hours)</label>
                <textarea value={form.next_action} onChange={e => setForm({...form, next_action: e.target.value})} rows={2} /></div>
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: 16 }}>
              {selected ? 'Update' : 'Save'} Qualification
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: 16 }}>Qualification History</h3>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Date</th><th>Prospect</th><th>Company</th><th>Money</th><th>DM</th><th>Pain</th><th>Proof</th><th>Sec.2</th><th>Decision</th></tr></thead>
            <tbody>
              {list.map(q => (
                <tr key={q.id} onClick={() => !isReadOnly && edit(q)} style={{ cursor: isReadOnly ? 'default' : 'pointer' }}>
                  <td>{new Date(q.qualification_date).toLocaleDateString()}</td>
                  <td><strong>{q.prospect_name}</strong></td>
                  <td>{q.prospect_company || '-'}</td>
                  <td>{statusBadge(q.box1_status)}</td>
                  <td>{statusBadge(q.box2_status)}</td>
                  <td>{statusBadge(q.box3_status)}</td>
                  <td><span className={`badge ${q.box4_proof_level === 'strong' ? 'badge-green' : q.box4_proof_level === 'adjacent' ? 'badge-amber' : 'badge-red'}`}>{q.box4_proof_level}</span></td>
                  <td>{statusBadge(q.box5_status)}</td>
                  <td>{decisionBadge(q.decision)}</td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No qualifications yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ PLAYBOOK ============
export const Playbook = () => {
  const [sections, setSections] = useState([]);
  useEffect(() => {
    api.get('/playbook').then(r => setSections(r.data.sections || [])).catch(console.error);
  }, []);

  return (
    <div>
      <div className="page-header">
        <h2><FileText size={24} style={{ marginRight: 8 }} /> Discovery Call Playbook</h2>
        <p>Path A — Prospect has a project</p>
      </div>
      {sections.map(s => (
        <div className="card" key={s.id}>
          <h3>{s.title}</h3>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#374151', marginTop: 12 }}>{s.content}</div>
        </div>
      ))}
      {sections.length === 0 && (
        <div className="card">
          <p style={{ color: '#6B7280' }}>Playbook sections will be added by Admin. The playbook content from the SoftRadix_Discovery_Call_Playbook.docx can be imported here section by section.</p>
        </div>
      )}
    </div>
  );
};

// ============ USER MANAGEMENT ============
export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'bde',
    can_access_upwork_referrals: false, claude_project_url: '',
  });
  // Per-row draft values for the inline Claude Project URL editor,
  // keyed by user id. Seeded from the loaded users so the input shows
  // the current value and only diverges while the admin is typing.
  const [urlDrafts, setUrlDrafts] = useState({});
  const [savingUrlId, setSavingUrlId] = useState(null);

  const loadUsers = async () => {
    const res = await api.get('/users');
    const list = res.data.users || [];
    setUsers(list);
    setUrlDrafts(Object.fromEntries(list.map(u => [u.id, u.claude_project_url || ''])));
  };

  useEffect(() => {
    loadUsers().catch(console.error);
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      await loadUsers();
      setShowAdd(false);
      setForm({
        name: '', email: '', password: '', role: 'bde',
        can_access_upwork_referrals: false, claude_project_url: '',
      });
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const saveClaudeUrl = async (id) => {
    setSavingUrlId(id);
    try {
      await api.put(`/users/${id}`, { claude_project_url: urlDrafts[id] ?? '' });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating Claude project URL');
    } finally {
      setSavingUrlId(null);
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      await api.put(`/users/${id}`, { is_active: !isActive });
      await loadUsers();
    } catch (err) { alert('Error updating user'); }
  };

  const toggleUpworkAccess = async (id, current) => {
    try {
      await api.put(`/users/${id}`, { can_access_upwork_referrals: !current });
      await loadUsers();
    } catch (err) { alert(err.response?.data?.error || 'Error updating Upwork access'); }
  };

  // admin/team_lead always have Upwork access regardless of the flag
  // (the backend gate is "role OR flag"). Disabling the toggle for them
  // avoids a confusing "off but still works" state in the UI.
  const upworkAccessImplicit = (role) => role === 'admin' || role === 'team_lead';

  const roleBadge = (role) => {
    const map = { admin: 'badge-red', business_manager: 'badge-blue', team_lead: 'badge-amber', bde: 'badge-green', viewer: 'badge-gray' };
    return <span className={`badge ${map[role] || 'badge-gray'}`}>{role?.replace('_', ' ')}</span>;
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div><h2>User Management</h2><p>Manage team access and roles</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}><UserPlus size={16} /> Add User</button>
      </div>

      {showAdd && (
        <div className="card">
          <form onSubmit={handleAdd}>
            <div className="form-row">
              <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
              <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required /></div>
              <div className="form-group"><label>Password</label><input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required /></div>
              <div className="form-group"><label>Role</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="bde">BDE</option><option value="team_lead">Team Lead</option>
                  <option value="business_manager">Business Manager</option><option value="admin">Admin</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select></div>
            </div>
            {/* admin/team_lead get Upwork access via role; the checkbox
                is only meaningful for the other three roles. */}
            {!upworkAccessImplicit(form.role) && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal' }}>
                  <input
                    type="checkbox"
                    checked={form.can_access_upwork_referrals}
                    onChange={(e) => setForm({...form, can_access_upwork_referrals: e.target.checked})}
                  />
                  Grant Upwork Referrals access
                </label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, marginLeft: 24 }}>
                  Lets this user see and work the Upwork Referrals queue. Admin and team_lead get this implicitly.
                </p>
              </div>
            )}
            <div className="form-group">
              <label>Claude Project URL (optional)</label>
              <input
                type="url"
                placeholder="https://claude.ai/project/…"
                value={form.claude_project_url}
                onChange={(e) => setForm({...form, claude_project_url: e.target.value})}
              />
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                The user's own "SoftRadix Sales Rebuild" Claude project. Used by the AI Workspace "Open Claude project" button. Leave blank to fall back to a fresh Claude chat.
              </p>
            </div>
            <button type="submit" className="btn btn-primary">Create User</button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Upwork access</th><th>Claude Project URL</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => {
                const implicit = upworkAccessImplicit(u.role);
                const effective = implicit || u.can_access_upwork_referrals;
                return (
                  <tr key={u.id}>
                    <td><strong>{u.name}</strong></td>
                    <td>{u.email}</td>
                    <td>{roleBadge(u.role)}</td>
                    <td>
                      {effective
                        ? <span className="badge badge-green">{implicit ? 'Via role' : 'Granted'}</span>
                        : <span className="badge badge-gray">No</span>}
                      {!implicit && u.is_active && (
                        <button
                          onClick={() => toggleUpworkAccess(u.id, u.can_access_upwork_referrals)}
                          style={{
                            marginLeft: 8, fontSize: 11, padding: '2px 8px',
                            background: 'none', border: '1px solid var(--gray-300)',
                            borderRadius: 4, cursor: 'pointer', color: 'var(--gray-700)',
                          }}
                        >
                          {u.can_access_upwork_referrals ? 'Revoke' : 'Grant'}
                        </button>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="url"
                          placeholder="https://claude.ai/new (fallback)"
                          value={urlDrafts[u.id] ?? ''}
                          onChange={(e) => setUrlDrafts({ ...urlDrafts, [u.id]: e.target.value })}
                          style={{ fontSize: 12, padding: '4px 6px', width: 220, border: '1px solid var(--gray-300)', borderRadius: 4 }}
                        />
                        <button
                          onClick={() => saveClaudeUrl(u.id)}
                          disabled={savingUrlId === u.id || (urlDrafts[u.id] ?? '') === (u.claude_project_url || '')}
                          style={{
                            fontSize: 11, padding: '4px 10px',
                            background: 'none', border: '1px solid var(--gray-300)',
                            borderRadius: 4, cursor: 'pointer', color: 'var(--gray-700)',
                          }}
                        >
                          {savingUrlId === u.id ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </td>
                    <td>{u.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">Inactive</span>}</td>
                    <td>
                      <button className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => toggleActive(u.id, u.is_active)}>
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ SETTINGS ============
// Default toggle state. New toggles added here are default-on
// for users whose stored config doesn't include them yet.
const DEFAULT_NOTIFICATION_TYPES = {
  instant_lead_alerts:      true,
  daily_summary_enabled:    true,
  weekly_summary_enabled:   true,
  monthly_summary_enabled:  true,
  summary_per_bde:          true,
  summary_pipeline:         true,
  summary_pending:          true,
  summary_mentions_enabled: true
};

const TOGGLE_LABELS = {
  instant_lead_alerts: {
    title: 'Instant lead alerts',
    desc: 'Post to Slack the moment a BDE adds a lead, and when an admin reclassifies it.'
  },
  daily_summary_enabled: {
    title: 'Daily team summary',
    desc: 'Sends a combined report at 10:00 PM IST every day.'
  },
  weekly_summary_enabled: {
    title: 'Weekly team summary',
    desc: 'Sends a Mon–Fri summary every Friday at 10:05 PM IST.'
  },
  monthly_summary_enabled: {
    title: 'Monthly team summary',
    desc: 'Sends a calendar-month summary on the last day of the month at 10:30 PM IST.'
  },
  summary_per_bde: {
    title: 'Per-BDE activity section',
    desc: 'Each BDE\'s sent / accepted / replies / handoffs / leads added.'
  },
  summary_pipeline: {
    title: 'Team pipeline section',
    desc: 'Total Hot / Warm / Cold validated leads.'
  },
  summary_pending: {
    title: 'Pending validation section',
    desc: 'Number of leads awaiting admin review (hidden when zero).'
  },
  summary_mentions_enabled: {
    title: '@mention BDEs in summaries',
    desc: 'Each BDE name is rendered as a Slack @mention so they get a personal notification. Falls back to plain name if their Slack ID isn\'t set.'
  }
};

const TOGGLE_GROUPS = [
  { title: 'Real-time alerts', keys: ['instant_lead_alerts'] },
  { title: 'Scheduled summaries', keys: ['daily_summary_enabled', 'weekly_summary_enabled', 'monthly_summary_enabled'] },
  { title: 'Summary content', keys: ['summary_per_bde', 'summary_pipeline', 'summary_pending', 'summary_mentions_enabled'] }
];

export const SettingsPage = () => {
  // Slack base config
  const [slack, setSlack] = useState({ webhook_url: '', channel_name: '', is_active: true });
  const [types, setTypes] = useState({ ...DEFAULT_NOTIFICATION_TYPES });
  const [savingSlack, setSavingSlack] = useState(false);
  const [slackError, setSlackError] = useState('');
  const [slackSavedAt, setSlackSavedAt] = useState(null);

  // Trigger state per cadence
  const [triggering, setTriggering] = useState({ daily: false, weekly: false, monthly: false });
  const [triggerResults, setTriggerResults] = useState({ daily: null, weekly: null, monthly: null });

  // Thresholds (unchanged)
  const [thresholds, setThresholds] = useState([]);
  const [newThreshold, setNewThreshold] = useState({ metric_name: '', threshold_type: 'below', threshold_value: '' });

  // AI analysis re-generation threshold. Stored in hours on the
  // backend; the UI lets the admin enter the value in hours OR days
  // and converts on save. Default unit picked on load: days when the
  // stored value is a clean multiple of 24h and >= 24h, else hours.
  const [aiThresholdValue, setAiThresholdValue] = useState('6');
  const [aiThresholdUnit, setAiThresholdUnit]   = useState('hours');
  const [aiThresholdHoursStored, setAiThresholdHoursStored] = useState(6);
  const [aiThresholdError, setAiThresholdError] = useState('');
  const [aiThresholdSavedAt, setAiThresholdSavedAt] = useState(null);
  const [aiThresholdSaving, setAiThresholdSaving] = useState(false);
  const [aiThresholdUpdatedBy, setAiThresholdUpdatedBy] = useState(null);
  const [aiThresholdUpdatedAt, setAiThresholdUpdatedAt] = useState(null);

  // AI Lead Diagnostic threshold — same shape as the team-analysis
  // threshold, separate setting key. Default 24h on the backend.
  const [diagThresholdValue, setDiagThresholdValue] = useState('24');
  const [diagThresholdUnit, setDiagThresholdUnit]   = useState('hours');
  const [diagThresholdHoursStored, setDiagThresholdHoursStored] = useState(24);
  const [diagThresholdError, setDiagThresholdError] = useState('');
  const [diagThresholdSavedAt, setDiagThresholdSavedAt] = useState(null);
  const [diagThresholdSaving, setDiagThresholdSaving] = useState(false);
  const [diagThresholdUpdatedBy, setDiagThresholdUpdatedBy] = useState(null);
  const [diagThresholdUpdatedAt, setDiagThresholdUpdatedAt] = useState(null);

  useEffect(() => {
    api.get('/config/slack').then(r => {
      if (r.data.config) {
        setSlack({
          webhook_url: r.data.config.webhook_url || '',
          channel_name: r.data.config.channel_name || '',
          is_active: !!r.data.config.is_active
        });
        // Merge stored toggles with defaults so newly added toggles get default-on behaviour
        setTypes({ ...DEFAULT_NOTIFICATION_TYPES, ...(r.data.config.notification_types || {}) });
      }
    }).catch(console.error);
    api.get('/config/thresholds').then(r => setThresholds(r.data.thresholds || [])).catch(console.error);
    api.get('/admin/settings/ai_analysis_threshold_hours')
      .then(r => {
        const s = r.data.setting;
        if (!s) return;
        const hours = parseInt(s.setting_value, 10);
        if (Number.isNaN(hours)) return;
        setAiThresholdHoursStored(hours);
        setAiThresholdUpdatedBy(s.updated_by_name || null);
        setAiThresholdUpdatedAt(s.updated_at || null);
        // Pick the friendlier unit. If it's an exact day multiple and
        // at least one day, show in days. Otherwise hours.
        if (hours >= 24 && hours % 24 === 0) {
          setAiThresholdUnit('days');
          setAiThresholdValue(String(hours / 24));
        } else {
          setAiThresholdUnit('hours');
          setAiThresholdValue(String(hours));
        }
      })
      .catch(err => {
        // 404 just means the seed row wasn't inserted; the backend
        // still falls back to 6h. Don't surface as an error.
        if (err.response?.status !== 404) console.error(err);
      });
    api.get('/admin/settings/lead_diagnostic_threshold_hours')
      .then(r => {
        const s = r.data.setting;
        if (!s) return;
        const hours = parseInt(s.setting_value, 10);
        if (Number.isNaN(hours)) return;
        setDiagThresholdHoursStored(hours);
        setDiagThresholdUpdatedBy(s.updated_by_name || null);
        setDiagThresholdUpdatedAt(s.updated_at || null);
        if (hours >= 24 && hours % 24 === 0) {
          setDiagThresholdUnit('days');
          setDiagThresholdValue(String(hours / 24));
        } else {
          setDiagThresholdUnit('hours');
          setDiagThresholdValue(String(hours));
        }
      })
      .catch(err => {
        if (err.response?.status !== 404) console.error(err);
      });
  }, []);

  const saveAiThreshold = async () => {
    setAiThresholdError('');
    setAiThresholdSavedAt(null);
    const n = parseFloat(aiThresholdValue);
    if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) {
      setAiThresholdError('Enter a whole number greater than 0.');
      return;
    }
    const hours = aiThresholdUnit === 'days' ? n * 24 : n;
    if (hours < 1 || hours > 720) {
      setAiThresholdError('Threshold must be between 1 hour and 30 days (720 hours).');
      return;
    }
    setAiThresholdSaving(true);
    try {
      const res = await api.put('/admin/settings/ai_analysis_threshold_hours', {
        value: String(hours),
        unit: 'hours',
      });
      setAiThresholdHoursStored(hours);
      setAiThresholdSavedAt(new Date());
      const s = res.data?.setting;
      if (s) {
        setAiThresholdUpdatedBy(s.updated_by_name || null);
        setAiThresholdUpdatedAt(s.updated_at || null);
      }
    } catch (err) {
      setAiThresholdError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setAiThresholdSaving(false);
    }
  };

  const saveDiagThreshold = async () => {
    setDiagThresholdError('');
    setDiagThresholdSavedAt(null);
    const n = parseFloat(diagThresholdValue);
    if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) {
      setDiagThresholdError('Enter a whole number greater than 0.');
      return;
    }
    const hours = diagThresholdUnit === 'days' ? n * 24 : n;
    if (hours < 1 || hours > 720) {
      setDiagThresholdError('Threshold must be between 1 hour and 30 days (720 hours).');
      return;
    }
    setDiagThresholdSaving(true);
    try {
      const res = await api.put('/admin/settings/lead_diagnostic_threshold_hours', {
        value: String(hours),
        unit: 'hours',
      });
      setDiagThresholdHoursStored(hours);
      setDiagThresholdSavedAt(new Date());
      const s = res.data?.setting;
      if (s) {
        setDiagThresholdUpdatedBy(s.updated_by_name || null);
        setDiagThresholdUpdatedAt(s.updated_at || null);
      }
    } catch (err) {
      setDiagThresholdError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setDiagThresholdSaving(false);
    }
  };

  const handleToggle = (key) => {
    setTypes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveSlack = async () => {
    setSlackError('');
    setSlackSavedAt(null);
    if (!slack.webhook_url.trim()) {
      setSlackError('Webhook URL is required.');
      return;
    }
    if (!slack.webhook_url.startsWith('https://hooks.slack.com/')) {
      setSlackError('Webhook URL should start with https://hooks.slack.com/');
      return;
    }
    setSavingSlack(true);
    try {
      await api.put('/config/slack', {
        webhook_url: slack.webhook_url.trim(),
        channel_name: slack.channel_name?.trim() || null,
        is_active: slack.is_active,
        notification_types: types
      });
      setSlackSavedAt(new Date());
    } catch (err) {
      setSlackError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSavingSlack(false);
    }
  };

  const handleTrigger = async (cadence) => {
    if (triggering[cadence]) return;
    setTriggerResults(prev => ({ ...prev, [cadence]: null }));
    setTriggering(prev => ({ ...prev, [cadence]: true }));
    try {
      const res = await api.post(`/config/slack/trigger-${cadence}`, {});
      setTriggerResults(prev => ({ ...prev, [cadence]: res.data }));
    } catch (err) {
      setTriggerResults(prev => ({
        ...prev,
        [cadence]: { sent: false, reason: 'error', error: err.response?.data?.error || err.message }
      }));
    } finally {
      setTriggering(prev => ({ ...prev, [cadence]: false }));
    }
  };

  const addThreshold = async () => {
    try {
      await api.post('/config/thresholds', newThreshold);
      const res = await api.get('/config/thresholds');
      setThresholds(res.data.thresholds || []);
      setNewThreshold({ metric_name: '', threshold_type: 'below', threshold_value: '' });
    } catch (err) { alert('Error adding threshold'); }
  };

  const maskedHelper = slack.webhook_url
    ? `Currently set to a webhook ending in …${slack.webhook_url.slice(-8)}`
    : 'No webhook configured yet.';

  const renderTriggerResult = (cadence) => {
    const r = triggerResults[cadence];
    if (!r) return null;
    return (
      <div
        className="card"
        style={{
          marginTop: 12,
          background: r.sent ? '#DCFCE7' : '#FEE2E2',
          color: r.sent ? '#166534' : '#991B1B'
        }}
      >
        {r.sent ? (
          <>
            <strong>✓ {cadence.charAt(0).toUpperCase() + cadence.slice(1)} summary sent.</strong> Check your Slack channel.
            {r.message && (
              <pre style={{
                marginTop: 12, padding: 12, background: 'rgba(255,255,255,0.6)',
                borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap',
                color: '#111827', fontFamily: 'inherit'
              }}>
                {r.message}
              </pre>
            )}
          </>
        ) : (
          <>
            <strong>✗ Not sent.</strong> Reason: {r.reason}
            {r.error && <div style={{ marginTop: 4, fontSize: 12 }}>{r.error}</div>}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header"><h2><SettingsIcon size={24} style={{ marginRight: 8 }} /> Settings</h2></div>

      {/* SLACK WEBHOOK */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>Slack Webhook</h3>

        {slackError && (
          <div style={{ background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AlertCircle size={16} /> {slackError}
          </div>
        )}
        {slackSavedAt && (
          <div style={{ background: '#DCFCE7', color: '#166534', padding: 12, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CheckCircle size={16} /> Saved at {slackSavedAt.toLocaleTimeString()}.
          </div>
        )}

        <div className="form-group">
          <label>Webhook URL</label>
          <input
            type="text"
            value={slack.webhook_url}
            onChange={e => setSlack({...slack, webhook_url: e.target.value})}
            placeholder="https://hooks.slack.com/services/..."
            autoComplete="off"
            spellCheck={false}
          />
          <p style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
            {maskedHelper} Treat this URL as a secret — anyone who has it can post to your channel.{' '}
            <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer"
               style={{ color: '#2E75B6', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={12} /> How to create one
            </a>
          </p>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Channel name (display only)</label>
            <input value={slack.channel_name || ''}
                   onChange={e => setSlack({...slack, channel_name: e.target.value})}
                   placeholder="#bd-reports" />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16 }}>
          <input type="checkbox" checked={slack.is_active}
                 onChange={e => setSlack({...slack, is_active: e.target.checked})} />
          Enable Slack notifications (master switch — turn off to silence everything)
        </label>
      </div>

      {/* NOTIFICATION TOGGLE GROUPS */}
      {TOGGLE_GROUPS.map((group) => (
        <div className="card" key={group.title}>
          <h3 style={{ marginBottom: 8 }}>{group.title}</h3>
          {group.keys.map((key) => {
            const meta = TOGGLE_LABELS[key];
            if (!meta) return null;
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: '1px solid #E5E7EB'
                }}
              >
                <input
                  type="checkbox"
                  id={`toggle_${key}`}
                  checked={!!types[key]}
                  onChange={() => handleToggle(key)}
                  style={{ width: 18, height: 18, marginTop: 2 }}
                />
                <label htmlFor={`toggle_${key}`} style={{ marginBottom: 0, flex: 1, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, color: '#1F2937' }}>{meta.title}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{meta.desc}</div>
                </label>
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 12, marginTop: 16, marginBottom: 24 }}>
        <button className="btn btn-primary" onClick={saveSlack} disabled={savingSlack}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Save size={16} /> {savingSlack ? 'Saving...' : 'Save Slack settings'}
        </button>
      </div>

      {/* MANUAL TEST */}
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>Send test summary</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
          Sends the report immediately using current toggles. Useful for verifying mentions and format before the cron fires. <em>Save changes first.</em>
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {['daily', 'weekly', 'monthly'].map((cadence) => (
            <button
              key={cadence}
              onClick={() => handleTrigger(cadence)}
              disabled={triggering[cadence]}
              className="btn btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Send size={16} />
              {triggering[cadence] ? 'Sending…' : `Send test ${cadence}`}
            </button>
          ))}
        </div>
        {renderTriggerResult('daily')}
        {renderTriggerResult('weekly')}
        {renderTriggerResult('monthly')}
      </div>

      {/* ALERT THRESHOLDS (unchanged) */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>Alert Thresholds</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>Get alerted when a BDE's metrics drop below these values.</p>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Metric</th><th>Condition</th><th>Value</th></tr></thead>
            <tbody>
              {thresholds.map(t => (
                <tr key={t.id}>
                  <td>{t.metric_name}</td>
                  <td>{t.threshold_type}</td>
                  <td>{t.threshold_value}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="form-row" style={{ marginTop: 16 }}>
          <div className="form-group"><label>Metric</label>
            <select value={newThreshold.metric_name} onChange={e => setNewThreshold({...newThreshold, metric_name: e.target.value})}>
              <option value="">Select...</option>
              <option value="acceptance_rate">Acceptance Rate</option>
              <option value="reply_rate">Reply Rate</option>
              <option value="daily_cr_sent">Daily CRs Sent</option>
            </select></div>
          <div className="form-group"><label>Threshold (%)</label>
            <input type="number" value={newThreshold.threshold_value}
              onChange={e => setNewThreshold({...newThreshold, threshold_value: e.target.value})} /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
            <button className="btn btn-primary btn-sm" onClick={addThreshold}>Add Threshold</button>
          </div>
        </div>
      </div>

      {/* AI ANALYSIS THRESHOLD */}
      <div className="card">
        <h3 style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="#3B82F6" /> AI Team Analysis
        </h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
          How long to wait before generating a fresh AI analysis for the same date range on Lead Performance.
          A click of <strong>Run AI Analysis</strong> sooner than this returns the latest cached analysis instead of calling Anthropic again —
          unless the lead pool has grown or any lead in range has been edited since.
        </p>

        {aiThresholdError && (
          <div style={{
            background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <AlertCircle size={16} /> {aiThresholdError}
          </div>
        )}
        {aiThresholdSavedAt && (
          <div style={{
            background: '#DCFCE7', color: '#166534', padding: 12, borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <CheckCircle size={16} /> Saved at {aiThresholdSavedAt.toLocaleTimeString()}.
          </div>
        )}

        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label>Re-analysis threshold</label>
            <input
              type="number"
              min="1"
              step="1"
              value={aiThresholdValue}
              onChange={(e) => setAiThresholdValue(e.target.value)}
              style={{ width: 120 }}
            />
          </div>
          <div className="form-group">
            <label>Unit</label>
            <select
              value={aiThresholdUnit}
              onChange={(e) => setAiThresholdUnit(e.target.value)}
              style={{ width: 120 }}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
            <button
              className="btn btn-primary"
              onClick={saveAiThreshold}
              disabled={aiThresholdSaving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Save size={16} /> {aiThresholdSaving ? 'Saving…' : 'Save threshold'}
            </button>
          </div>
        </div>

        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
          Currently stored: <strong>{aiThresholdHoursStored} hour{aiThresholdHoursStored === 1 ? '' : 's'}</strong>
          {aiThresholdHoursStored >= 24 && aiThresholdHoursStored % 24 === 0 && (
            <> (= {aiThresholdHoursStored / 24} day{aiThresholdHoursStored === 24 ? '' : 's'})</>
          )}
          . Min 1 hour, max 30 days.
          {aiThresholdUpdatedBy && aiThresholdUpdatedAt && (
            <> Last updated by {aiThresholdUpdatedBy} on {new Date(aiThresholdUpdatedAt).toLocaleString()}.</>
          )}
        </p>
      </div>

      {/* AI LEAD DIAGNOSTIC THRESHOLD */}
      <div className="card">
        <h3 style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="#3B82F6" /> AI Lead Diagnostic
        </h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
          How long to wait before generating a fresh per-lead diagnostic on Lead Detail.
          A click of <strong>Run AI Diagnostic</strong> sooner than this returns the latest cached diagnostic instead of calling Anthropic again —
          unless the lead has been edited since.
        </p>

        {diagThresholdError && (
          <div style={{
            background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <AlertCircle size={16} /> {diagThresholdError}
          </div>
        )}
        {diagThresholdSavedAt && (
          <div style={{
            background: '#DCFCE7', color: '#166534', padding: 12, borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <CheckCircle size={16} /> Saved at {diagThresholdSavedAt.toLocaleTimeString()}.
          </div>
        )}

        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label>Re-analysis threshold</label>
            <input
              type="number"
              min="1"
              step="1"
              value={diagThresholdValue}
              onChange={(e) => setDiagThresholdValue(e.target.value)}
              style={{ width: 120 }}
            />
          </div>
          <div className="form-group">
            <label>Unit</label>
            <select
              value={diagThresholdUnit}
              onChange={(e) => setDiagThresholdUnit(e.target.value)}
              style={{ width: 120 }}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
            <button
              className="btn btn-primary"
              onClick={saveDiagThreshold}
              disabled={diagThresholdSaving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Save size={16} /> {diagThresholdSaving ? 'Saving…' : 'Save threshold'}
            </button>
          </div>
        </div>

        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
          Currently stored: <strong>{diagThresholdHoursStored} hour{diagThresholdHoursStored === 1 ? '' : 's'}</strong>
          {diagThresholdHoursStored >= 24 && diagThresholdHoursStored % 24 === 0 && (
            <> (= {diagThresholdHoursStored / 24} day{diagThresholdHoursStored === 24 ? '' : 's'})</>
          )}
          . Min 1 hour, max 30 days.
          {diagThresholdUpdatedBy && diagThresholdUpdatedAt && (
            <> Last updated by {diagThresholdUpdatedBy} on {new Date(diagThresholdUpdatedAt).toLocaleString()}.</>
          )}
        </p>
      </div>
    </div>
  );
};

// ============ NOTIFICATIONS ============
export const Notifications = () => {
  const { isReadOnly } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markAllRead = async () => {
    await api.put('/notifications/all/read');
    fetchNotifications();
  };

  const typeIcon = {
    missing_daily_log: '⚠️', guideline_update: '📋', review_note: '💬',
    comment_response: '💡', threshold_alert: '🚨', weekly_report_ready: '📊', general: '🔔'
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div><h2><Bell size={24} style={{ marginRight: 8 }} /> Notifications</h2>
          <p>{unread} unread</p></div>
        {!isReadOnly && unread > 0 && <button className="btn btn-secondary btn-sm" onClick={markAllRead}><CheckCircle size={14} /> Mark All Read</button>}
      </div>

      {notifications.map(n => (
        <div className="card" key={n.id} style={{ background: n.is_read ? 'white' : '#EAF3F8', borderLeft: n.is_read ? 'none' : '4px solid #2E75B6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <span style={{ marginRight: 8 }}>{typeIcon[n.notification_type] || '🔔'}</span>
              <strong>{n.title}</strong>
            </div>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>{new Date(n.created_at).toLocaleString()}</span>
          </div>
          <p style={{ fontSize: 14, color: '#4B5563', marginTop: 4 }}>{n.message}</p>
        </div>
      ))}
      {notifications.length === 0 && <div className="empty-state"><p>No notifications yet.</p></div>}
    </div>
  );
};

// ============ AI COST DASHBOARD (Admin only) ============
//
// Admin-only spend visibility powered by Anthropic's Admin Cost Report
// API. Reads from /admin/ai-cost/report (server-side calls Anthropic
// with ANTHROPIC_ADMIN_API_KEY) and joins to /admin/ai-cost/mappings
// for human labels (e.g. "aiReviewService"). Two empty states:
//   • admin_key_configured:false  →  "Connect admin key" instructions
//   • mappings.length === 0       →  "Add at least one workspace mapping"
//
// Charts: the per-day stacked-bar viz is hand-rolled CSS (one column
// per day, colored segments per feature, height = total). No chart
// library — the project doesn't ship one and the data is small.
// ============================================================

const COST_PRESETS = [
  { key: 'last_7d',  label: 'Last 7 days',  days: 7  },
  { key: 'last_30d', label: 'Last 30 days', days: 30 },
  { key: 'last_90d', label: 'Last 90 days', days: 90 },
  { key: 'custom',   label: 'Custom range', days: null },
];

// Fixed palette assigned to features in stable alphabetical order so a
// feature's color doesn't shift between page loads as new mappings land.
const FEATURE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#EF4444', '#6366F1'];

const fmtUsd = (cents) => {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '—';
  const dollars = cents / 100;
  if (dollars < 0.01 && dollars > 0) return '<$0.01';
  return `$${dollars.toFixed(2)}`;
};

const fmtDateShort = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Build start/end ISO timestamps from a preset key (UTC day boundaries).
//
// CRITICAL: both timestamps must be STABLE across renders. The earlier
// version used `new Date().toISOString()` for the end, which changes
// every millisecond and turned the [range.start, range.end] useEffect
// dependency into an infinite re-fetch loop (500+ pending requests in
// seconds, none ever completing). Snap both ends to UTC day boundaries
// — the Cost Report endpoint's bucket_width is 1d so sub-day precision
// is pointless anyway.
const buildRange = (preset, custom) => {
  const now = new Date();
  const todayUtc = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    23, 59, 59, 0,
  ));
  const endIso = todayUtc.toISOString();
  if (preset === 'custom') {
    const start = custom.start ? `${custom.start}T00:00:00Z` : null;
    const end   = custom.end   ? `${custom.end}T23:59:59Z`   : null;
    return { start, end };
  }
  const cfg = COST_PRESETS.find((p) => p.key === preset) || COST_PRESETS[1];
  const start = new Date(todayUtc);
  start.setUTCDate(start.getUTCDate() - cfg.days);
  start.setUTCHours(0, 0, 0, 0);
  return { start: start.toISOString(), end: endIso };
};

// ============================================================
// MAPPING ROW — table-row component for the editor section
// ============================================================
const MappingRow = ({ mapping, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    anthropic_id: mapping.anthropic_id,
    kind: mapping.kind,
    feature_label: mapping.feature_label,
    notes: mapping.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const res = await api.put(`/admin/ai-cost/mappings/${mapping.id}`, form);
      onSaved(res.data.mapping);
      setEditing(false);
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the "${mapping.feature_label}" mapping?`)) return;
    try {
      await api.delete(`/admin/ai-cost/mappings/${mapping.id}`);
      onDeleted(mapping.id);
    } catch (e) {
      setErr(e.response?.data?.error || 'Delete failed.');
    }
  };

  if (editing) {
    return (
      <tr style={{ background: '#F9FAFB' }}>
        <td style={{ padding: '8px 10px' }}>
          <input value={form.anthropic_id} onChange={(e) => setForm({ ...form, anthropic_id: e.target.value })}
                 placeholder="wrkspc_..." style={{ width: '100%' }} />
        </td>
        <td style={{ padding: '8px 10px' }}>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} style={{ width: '100%' }}>
            <option value="workspace">workspace</option>
            <option value="api_key">api_key</option>
          </select>
        </td>
        <td style={{ padding: '8px 10px' }}>
          <input value={form.feature_label} onChange={(e) => setForm({ ...form, feature_label: e.target.value })}
                 placeholder="aiReviewService" style={{ width: '100%' }} />
        </td>
        <td style={{ padding: '8px 10px' }}>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                 placeholder="optional notes" style={{ width: '100%' }} />
        </td>
        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {' '}
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          {err && <div style={{ color: '#991B1B', fontSize: 12, marginTop: 4 }}>{err}</div>}
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 13 }}>{mapping.anthropic_id}</td>
      <td style={{ padding: '8px 10px', fontSize: 13 }}>{mapping.kind}</td>
      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{mapping.feature_label}</td>
      <td style={{ padding: '8px 10px', fontSize: 13, color: '#6B7280' }}>{mapping.notes || '—'}</td>
      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
          <Edit2 size={12} /> Edit
        </button>
        {' '}
        <button className="btn btn-secondary btn-sm" onClick={remove}>
          <Trash2 size={12} /> Delete
        </button>
      </td>
    </tr>
  );
};

// ============================================================
// ADD MAPPING FORM — inline form below the mapping table
// ============================================================
const AddMappingForm = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ anthropic_id: '', kind: 'workspace', feature_label: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      const res = await api.post('/admin/ai-cost/mappings', form);
      onAdded(res.data.mapping);
      setForm({ anthropic_id: '', kind: 'workspace', feature_label: '', notes: '' });
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.error || 'Add failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
        <Plus size={14} /> Add mapping
      </button>
    );
  }

  return (
    <div style={{ marginTop: 12, padding: 12, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6 }}>
      <div className="form-row">
        <div className="form-group">
          <label>Anthropic ID</label>
          <input value={form.anthropic_id} onChange={(e) => setForm({ ...form, anthropic_id: e.target.value })}
                 placeholder="wrkspc_..." />
        </div>
        <div className="form-group">
          <label>Kind</label>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="workspace">workspace</option>
            <option value="api_key">api_key</option>
          </select>
        </div>
        <div className="form-group">
          <label>Feature label</label>
          <input value={form.feature_label} onChange={(e) => setForm({ ...form, feature_label: e.target.value })}
                 placeholder="aiReviewService" />
        </div>
        <div className="form-group">
          <label>Notes (optional)</label>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
          {saving ? 'Adding…' : 'Add mapping'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setErr(''); }}>
          <X size={12} /> Cancel
        </button>
      </div>
      {err && (
        <div style={{ marginTop: 8, color: '#991B1B', fontSize: 13 }}>{err}</div>
      )}
    </div>
  );
};

// ============================================================
// STACKED BAR — one column per day, colored segments per feature.
// Total bar height scales to the max-cost day in the window so the
// shape of the trend is readable even at low absolute spend.
// ============================================================
const StackedBarChart = ({ buckets, featureColorMap }) => {
  if (!buckets.length) {
    return (
      <div style={{ fontSize: 13, color: '#6B7280', padding: 12 }}>
        No data in this range.
      </div>
    );
  }
  const maxBucket = buckets.reduce((m, b) => Math.max(m, b.total_cents), 0);
  const safeMax = maxBucket || 1;
  const chartHeight = 160;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 4,
        height: chartHeight, padding: '8px 0',
        borderBottom: '1px solid #E5E7EB',
      }}>
        {buckets.map((b) => {
          const totalH = Math.max(2, Math.round((b.total_cents / safeMax) * (chartHeight - 16)));
          const segments = Object.entries(b.by_feature || {});
          // Stack mapped features first, then the unmapped segment.
          const stacked = [
            ...segments.map(([label, cents]) => ({
              label, cents, color: featureColorMap.get(label) || '#9CA3AF',
            })),
            ...(b.unmapped_cents > 0
              ? [{ label: 'Unmapped', cents: b.unmapped_cents, color: '#D1D5DB' }]
              : []),
          ];
          return (
            <div key={b.starting_at} style={{
              flex: 1, display: 'flex', flexDirection: 'column-reverse',
              alignItems: 'stretch', minWidth: 0,
            }} title={`${fmtDateShort(b.starting_at)} — ${fmtUsd(b.total_cents)}`}>
              <div style={{ height: totalH, display: 'flex', flexDirection: 'column-reverse' }}>
                {stacked.map((s, i) => {
                  const h = Math.max(0, Math.round((s.cents / b.total_cents) * totalH));
                  return (
                    <div key={i} style={{
                      background: s.color, height: h,
                      borderTop: i > 0 ? '1px solid rgba(255,255,255,0.6)' : 'none',
                    }} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        display: 'flex', gap: 4, fontSize: 10, color: '#9CA3AF',
        marginTop: 4,
      }}>
        {buckets.map((b, i) => (
          <div key={b.starting_at} style={{
            flex: 1, textAlign: 'center', minWidth: 0,
            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            visibility: (i === 0 || i === buckets.length - 1 || i % Math.ceil(buckets.length / 8) === 0) ? 'visible' : 'hidden',
          }}>{fmtDateShort(b.starting_at)}</div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// COST DASHBOARD MAIN
// ============================================================
export const CostDashboard = () => {
  const [preset, setPreset] = useState('last_30d');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [report, setReport] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const range = buildRange(preset, custom);

  // Initial load + range changes. The fetch is inlined to keep the
  // dependency list explicit (matches the LeadPerformance pattern in
  // this repo, which keeps the effect self-contained).
  useEffect(() => {
    if (!range.start) return;
    setLoading(true);
    setError('');
    api.get('/admin/ai-cost/report', {
      params: { start: range.start, end: range.end || undefined },
      timeout: 60000, // upstream Anthropic call can be slow on long ranges
    })
      .then((res) => {
        setReport(res.data);
        setMappings(res.data.mappings || []);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load cost report.'))
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  // Also keep mappings fresh independently — needed for the empty-state
  // CTAs even when the report fetch errors (e.g. admin key missing but
  // we still want to show the mapping editor so the admin can prep).
  useEffect(() => {
    api.get('/admin/ai-cost/mappings')
      .then(r => setMappings(r.data.mappings || []))
      .catch(() => {});
  }, []);

  // Refresh button — bypasses the in-process cache on the backend.
  const refresh = async () => {
    if (!range.start) return;
    setRefreshing(true);
    setError('');
    try {
      const res = await api.get('/admin/ai-cost/report', {
        params: { start: range.start, end: range.end || undefined, refresh: '1' },
        timeout: 60000,
      });
      setReport(res.data);
      setMappings(res.data.mappings || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load cost report.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleMappingAdded = (m) => setMappings([...mappings, m].sort((a, b) => a.feature_label.localeCompare(b.feature_label)));
  const handleMappingSaved = (m) => setMappings(mappings.map((x) => x.id === m.id ? m : x).sort((a, b) => a.feature_label.localeCompare(b.feature_label)));
  const handleMappingDeleted = (id) => setMappings(mappings.filter((x) => x.id !== id));

  // Feature → color map. Stable across re-renders because feature
  // order is alphabetical on the mapping list.
  const featureColorMap = new Map();
  [...mappings]
    .map((m) => m.feature_label)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
    .forEach((label, i) => featureColorMap.set(label, FEATURE_COLORS[i % FEATURE_COLORS.length]));

  return (
    <div>
      <div className="page-header" style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <DollarSign size={22} color="#10B981" /> AI Cost
          </h2>
          <p>Per-feature spend on Anthropic, sourced from the Admin Cost Report API.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Range</label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, background: '#FFFFFF', cursor: 'pointer' }}>
              {COST_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Start</label>
                <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                       style={{ padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>End</label>
                <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                       style={{ padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }} />
              </div>
            </>
          )}
          <button onClick={refresh} disabled={refreshing || loading}
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RotateCcw size={14} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: 12, background: '#FEE2E2', color: '#991B1B',
          borderRadius: 6, fontSize: 14, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Admin key missing — empty state with instructions */}
      {report && report.admin_key_configured === false && (
        <div className="card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#92400E' }}>
            <KeyRound size={18} /> Connect the admin key
          </h3>
          <p style={{ fontSize: 14, color: '#374151' }}>
            The cost dashboard needs an organization-level admin API key (different from the per-feature keys).
            Create one in <strong>Anthropic Console → Settings → Admin Keys</strong> (the key starts with{' '}
            <code style={{ background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>sk-ant-admin-</code>).
            Set it on the backend as <code style={{ background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>ANTHROPIC_ADMIN_API_KEY</code> in
            {' '}<code style={{ background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>backend/.env</code>, then{' '}
            <code style={{ background: '#FEF3C7', padding: '1px 6px', borderRadius: 4 }}>pm2 restart sales-tool-api</code>.
          </p>
          <p style={{ fontSize: 13, color: '#92400E' }}>{report.message}</p>
        </div>
      )}

      {/* Loading */}
      {loading && !report && (
        <div className="empty-state"><p>Loading cost report…</p></div>
      )}

      {/* Totals + breakdown + chart (only when we have real data) */}
      {report && report.admin_key_configured && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 16 }}>
            <div className="card" style={{ borderLeft: '4px solid #10B981' }}>
              <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total spend</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>{fmtUsd(report.totals?.total_cents || 0)}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {report.buckets?.length || 0} day{report.buckets?.length === 1 ? '' : 's'} in range
              </div>
            </div>
            <div className="card">
              <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mapped to features</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>
                {fmtUsd((report.by_feature || []).reduce((s, f) => s + f.cost_cents, 0))}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {(report.by_feature || []).length} feature{(report.by_feature || []).length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="card" style={{ borderLeft: (report.unmapped || []).length > 0 ? '4px solid #F59E0B' : undefined }}>
              <div style={{ fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unmapped</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: (report.unmapped || []).length > 0 ? '#92400E' : '#111827' }}>
                {fmtUsd((report.unmapped || []).reduce((s, u) => s + u.cost_cents, 0))}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {(report.unmapped || []).length} workspace{(report.unmapped || []).length === 1 ? '' : 's'} without a label
              </div>
            </div>
          </div>

          {/* Per-feature breakdown */}
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Per-feature spend</h3>
            {(report.by_feature || []).length === 0 ? (
              <p style={{ fontSize: 13, color: '#6B7280' }}>
                No mapped features in this range. Add a workspace mapping below so spend gets labelled.
              </p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Feature</th><th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>% of total</th></tr></thead>
                  <tbody>
                    {(report.by_feature || []).map((row) => {
                      const pct = report.totals?.total_cents ? Math.round((row.cost_cents / report.totals.total_cents) * 100) : 0;
                      return (
                        <tr key={row.label}>
                          <td>
                            <span style={{
                              display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                              background: featureColorMap.get(row.label) || '#9CA3AF',
                              marginRight: 8, verticalAlign: 'middle',
                            }} />
                            <strong>{row.label}</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>{fmtUsd(row.cost_cents)}</td>
                          <td style={{ textAlign: 'right', color: '#6B7280' }}>{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Trend chart */}
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Daily trend</h3>
            <StackedBarChart buckets={report.buckets || []} featureColorMap={featureColorMap} />
          </div>

          {/* Unmapped warning */}
          {(report.unmapped || []).length > 0 && (
            <div className="card" style={{ marginTop: 16, borderLeft: '4px solid #F59E0B', background: '#FFFBEB' }}>
              <h3 style={{ color: '#92400E', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={18} /> Unmapped workspaces
              </h3>
              <p style={{ fontSize: 13, color: '#92400E' }}>
                These workspaces show spend in this range but aren't labelled in the mapping table.
                Add a mapping row below to attribute their cost to a feature.
              </p>
              <table>
                <thead><tr><th>Workspace ID</th><th style={{ textAlign: 'right' }}>Cost in range</th></tr></thead>
                <tbody>
                  {(report.unmapped || []).map((u) => (
                    <tr key={u.workspace_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.workspace_id}</td>
                      <td style={{ textAlign: 'right' }}>{fmtUsd(u.cost_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.fetched_at && (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, textAlign: 'right' }}>
              Fetched {new Date(report.fetched_at).toLocaleString()}
              {report.from_cache && ' · cached (refresh to force)'}
            </div>
          )}
        </>
      )}

      {/* Mapping editor — always shown, even when admin key isn't set,
          so the admin can prep mappings before the key is in place. */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={18} color="#6B7280" /> Workspace → feature mapping
        </h3>
        <p style={{ fontSize: 13, color: '#6B7280' }}>
          Map each Anthropic workspace_id (or api_key_id) to a human label so the cost report rows are readable.
          The expected labels are the service names: <code>aiReviewService</code>, <code>aiDiagnosticService</code>,
          <code>aiTeamAnalysisService</code>, <code>aiFollowUpReviewService</code>.
        </p>
        {mappings.length === 0 ? (
          <div className="empty-state" style={{ background: '#F9FAFB' }}>
            <p>No mappings yet. Add one for each Anthropic workspace.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Anthropic ID</th><th>Kind</th><th>Feature label</th><th>Notes</th><th>Actions</th></tr></thead>
              <tbody>
                {mappings.map((m) => (
                  <MappingRow key={m.id} mapping={m}
                              onSaved={handleMappingSaved}
                              onDeleted={handleMappingDeleted} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AddMappingForm onAdded={handleMappingAdded} />
      </div>
    </div>
  );
};

// ============ MONTHLY PERFORMANCE REPORT ============
// "June 2026" from a "2026-06" report_month key.
const monthLabelFromKey = (key) => {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  const name = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long' });
  return `${name} ${y}`;
};

export const MonthlyReport = () => {
  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [current, setCurrent] = useState(null); // full row incl. report_text
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load the list once; auto-select the newest report so the page opens on
  // something useful.
  useEffect(() => {
    api.get('/monthly-reports')
      .then((r) => {
        const list = r.data.reports || [];
        setReports(list);
        if (list.length) setSelectedId(list[0].id);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load reports.'))
      .finally(() => setLoading(false));
  }, []);

  // Load the selected report's full text whenever the selection changes.
  useEffect(() => {
    if (!selectedId) { setCurrent(null); return; }
    api.get(`/monthly-reports/${selectedId}`)
      .then((r) => setCurrent(r.data.report))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'));
  }, [selectedId]);

  // Copy all → clipboard, then mark the report done (stops the popup nag).
  const copyAll = async () => {
    if (!current) return;
    try {
      await navigator.clipboard.writeText(current.report_text);
      toast.success('Report copied to clipboard.');
    } catch {
      toast.error('Could not copy. Use "Download as .txt" instead.');
      return; // don't mark done if the copy itself failed
    }
    try {
      const res = await api.post(`/monthly-reports/${current.id}/mark-done`);
      const updated = res.data.report;
      setCurrent(updated);
      setReports((rs) => rs.map((r) => (r.id === updated.id ? { ...r, status: updated.status, acknowledged_at: updated.acknowledged_at } : r)));
    } catch {
      // Non-fatal: the copy succeeded, the status bump didn't. The popup may
      // re-nag but a re-copy will settle it.
    }
  };

  // Download fallback for the ~180KB+ case where clipboard is flaky.
  const download = () => {
    if (!current) return;
    const blob = new Blob([current.report_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `softradix-monthly-report-${current.report_month}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const statusBadge = (status) => {
    const map = {
      ready:   { bg: '#FEF3C7', color: '#92400E', label: 'Ready' },
      done:    { bg: '#D1FAE5', color: '#065F46', label: 'Copied' },
      expired: { bg: '#F3F4F6', color: '#6B7280', label: 'Expired' },
    };
    const s = map[status] || map.expired;
    return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{s.label}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={22} color="#6366F1" /> Monthly Performance Report
        </h2>
        <p>Analysis-ready, end-of-month snapshot of the whole team. Copy it and paste into Claude for a full performance review.</p>
      </div>

      {error && <div className="card" style={{ color: '#B91C1C' }}>{error}</div>}
      {loading && <div className="card"><p style={{ color: '#6B7280' }}>Loading…</p></div>}

      {!loading && reports.length === 0 && (
        <div className="card">
          <p style={{ color: '#6B7280' }}>No reports yet. The first one generates automatically on the last weekday of the month.</p>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <>
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Report month</label>
              <select value={selectedId || ''} onChange={(e) => setSelectedId(Number(e.target.value))}
                      style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, background: '#FFFFFF', cursor: 'pointer' }}>
                {reports.map((r) => (
                  <option key={r.id} value={r.id}>{monthLabelFromKey(r.report_month)}</option>
                ))}
              </select>
            </div>
            {current && <div style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>{statusBadge(current.status)}</div>}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <button onClick={copyAll} className="btn btn-primary" disabled={!current}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Copy size={16} /> Copy all
              </button>
              <button onClick={download} className="btn btn-secondary" disabled={!current}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Download size={16} /> Download as .txt
              </button>
            </div>
          </div>

          {current && (
            <div className="card">
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 10 }}>
                Generated {current.generated_at ? new Date(current.generated_at).toLocaleString() : '—'}
                {current.acknowledged_at && ` · copied ${new Date(current.acknowledged_at).toLocaleString()}`}
              </div>
              <pre style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: '#111827', background: '#F9FAFB', border: '1px solid #E5E7EB',
                borderRadius: 8, padding: 16, maxHeight: '70vh', overflow: 'auto', margin: 0,
              }}>{current.report_text}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};
